<template>
  <div class="average-score-trend-chart">
    <div v-if="!hasData" class="no-data-tip">暂无数据</div>
    <div v-else id="averageScoreTrendChart" ref="chartRef" class="chart-content"></div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, watch, nextTick, onUnmounted } from 'vue';
import * as echarts from 'echarts';

// 定义props
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  }
});

const chartRef = ref(null);
let chartInstance = null;

// Mock数据，确保图表能正常显示
const mockTrendData = [
  { name: '2023春季体测', score: 75 },
  { name: '2023秋季体测', score: 80 },
  { name: '2023冬季体测', score: 85 }
];

// 检查是否有数据
const hasData = ref(mockTrendData.length > 0);

// 初始化图表
const initChart = () => {
  // 即使没有DOM引用也创建图表实例，确保在DOM可用时能立即渲染
  if (!chartRef.value) {
    // 设置一个短暂的延时后重试
    setTimeout(() => {
      if (chartRef.value) {
        initChart();
      }
    }, 100);
    return;
  }
  
  // 销毁已存在的图表实例
  if (chartInstance) {
    chartInstance.dispose();
  }
  
  chartInstance = echarts.init(chartRef.value);
  const chartData = props.data.length > 0 ? props.data : mockTrendData;
  hasData.value = chartData.length > 0;
  
  if (!hasData.value) return;
  
  const option = {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c}分'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.map(item => item.name),
      axisLabel: {
        interval: 0,
        rotate: 30
      }
    },
    yAxis: {
      type: 'value',
      name: '平均分',
      min: 0,
      max: 100,
      axisLabel: {
        formatter: '{value}分'
      }
    },
    series: [
      {
        name: '平均成绩',
        type: 'line',
        smooth: true,
        data: chartData.map(item => item.score),
        itemStyle: {
          color: '#1890ff'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: 'rgba(24, 144, 255, 0.6)'
            },
            {
              offset: 1,
              color: 'rgba(24, 144, 255, 0.1)'
            }
          ])
        },
        markLine: {
          silent: true,
          data: [
            {
              yAxis: 90,
              lineStyle: {
                color: '#52c41a'
              },
              label: {
                formatter: '优秀线:90分',
                position: 'insideEndTop'
              }
            },
            {
              yAxis: 80,
              lineStyle: {
                color: '#faad14'
              },
              label: {
                formatter: '良好线:80分',
                position: 'insideEndTop'
              }
            },
            {
              yAxis: 60,
              lineStyle: {
                color: '#ff4d4f'
              },
              label: {
                formatter: '及格线:60分',
                position: 'insideEndTop'
              }
            }
          ]
        }
      }
    ]
  };
  
  chartInstance.setOption(option);
  
  // 强制重绘一次
  chartInstance.resize();
};

// 主动触发图表重绘的方法，供父组件调用
const triggerRedraw = () => {
  if (chartInstance) {
    chartInstance.resize();
  } else {
    // 如果实例不存在，重新初始化
    initChart();
  }
};

// 暴露方法给父组件
defineExpose({
  triggerRedraw
});

// 监听数据变化
watch(
  () => props.data,
  () => {
    nextTick(() => {
      initChart();
    });
  },
  { deep: true }
);

// 处理窗口大小变化
const handleResize = () => {
  if (chartInstance) {
    chartInstance.resize();
  }
};

onMounted(() => {
  nextTick(() => {
    const chartData = props.data.length > 0 ? props.data : mockTrendData;
    hasData.value = chartData.length > 0;
    initChart();
    window.addEventListener('resize', handleResize);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style scoped>
.average-score-trend-chart {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.chart-content {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

.no-data-tip {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #999;
  font-size: 14px;
  background-color: #fafafa;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .average-score-trend-chart {
    height: 300px;
  }
}
</style>